import React, { useState, useEffect, useRef, useCallback } from 'react';
import { stravaActivityService } from '../../services/StravaActivityService';
import ActivityGridSettings from './ActivityGridSettings';
import BurgerMenu from './BurgerMenu';
import html2canvas from 'html2canvas';
import './ActivityGrid.css';
import './ActivityGridSettings.css';
import './BurgerMenu.css';

const ActivityCard = ({ activity, settings }) => {
    const formatDuration = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    const formatDistance = (meters) => {
        return (meters / 1000).toFixed(1) + 'km';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    const renderRoute = () => {
        console.log('Activity map data:', activity.map);
        if (!activity.map?.summary_polyline) {
            console.log('No polyline data available');
            return null;
        }

        // Decode the polyline
        const points = decodePolyline(activity.map.summary_polyline);
        console.log('Decoded points:', points.length);
        
        // Calculate bounds
        const bounds = points.reduce((acc, point) => ({
            minLat: Math.min(acc.minLat, point.lat),
            maxLat: Math.max(acc.maxLat, point.lat),
            minLng: Math.min(acc.minLng, point.lng),
            maxLng: Math.max(acc.maxLng, point.lng)
        }), {
            minLat: points[0].lat,
            maxLat: points[0].lat,
            minLng: points[0].lng,
            maxLng: points[0].lng
        });

        console.log('Route bounds:', bounds);

        // Calculate scale and offset
        const width = 280;
        const height = 180;
        const padding = 20;
        const latRange = bounds.maxLat - bounds.minLat;
        const lngRange = bounds.maxLng - bounds.minLng;
        const scale = Math.min(
            (width - padding * 2) / lngRange,
            (height - padding * 2) / latRange
        ) * 0.9;

        console.log('Scale:', scale);

        // Transform points to SVG coordinates
        const svgPoints = points.map(point => ({
            x: (point.lng - bounds.minLng) * scale + padding + (width - lngRange * scale) / 2,
            y: height - ((point.lat - bounds.minLat) * scale + padding + (height - latRange * scale) / 2)
        }));

        return (
            <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${width} ${height}`}
                style={{
                    position: 'absolute',
                    top: '10%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1,
                    opacity: 1,
                    pointerEvents: 'none',
                    width: '80%',
                    height: '80%'
                }}
            >
                <path
                    d={`M ${svgPoints.map(p => `${p.x},${p.y}`).join(' L ')}`}
                    fill="none"
                    stroke={settings.colors.polyline}
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        );
    };

    return (
        <div 
            className="activity-card"
            style={{
                backgroundImage: activity.photos?.primary ? `url(${stravaActivityService.getPhotoUrl(activity.photos.primary.id)})` : 'none',
                backgroundColor: settings.colors.cardBackground
            }}
        >
            {renderRoute()}
            <div className="activity-info" style={{ color: settings.colors.text }}>
                <div className="activity-details">
                    <p>{activity.name}</p>
                    <p>{formatDistance(activity.distance)}</p>
                    <p>{formatDuration(activity.moving_time)}</p>
                    <p>{formatDate(activity.start_date)}</p>
                </div>
            </div>
        </div>
    );
};

// Polyline decoder function
function decodePolyline(str) {
    const points = [];
    let index = 0;
    let lat = 0;
    let lng = 0;

    while (index < str.length) {
        let shift = 0;
        let result = 0;

        do {
            let b = str.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (result >= 0x20);

        lat += ((result & 1) ? ~(result >> 1) : (result >> 1));

        shift = 0;
        result = 0;

        do {
            let b = str.charCodeAt(index++) - 63;
            result |= (b & 0x1f) << shift;
            shift += 5;
        } while (result >= 0x20);

        lng += ((result & 1) ? ~(result >> 1) : (result >> 1));

        points.push({ lat: lat * 1e-5, lng: lng * 1e-5 });
    }

    return points;
}

const ActivityGrid = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [rateLimitStatus, setRateLimitStatus] = useState({ current: 0, limit: 100 });
    const [settings, setSettings] = useState({
        colors: {
            background: '#000000',
            cardBackground: '#111111',
            text: '#FFFFFF',
            polyline: '#FF0000'
        },
        title: 'My Strava Activities',
        dateRange: {
            start: new Date(new Date().setMonth(new Date().getMonth() - 1)),
            end: new Date()
        }
    });
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const gridRef = useRef(null);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const loadActivities = useCallback(async () => {
        try {
            setLoading(true);
            let allActivities = [];
            let page = 1;
            let hasMore = true;

            // Ensure dates are properly formatted
            const startDate = settings.dateRange.start ? new Date(settings.dateRange.start) : null;
            const endDate = settings.dateRange.end ? new Date(settings.dateRange.end) : new Date();

            // Fetch all pages until we have all activities in the date range
            while (hasMore) {
                const activities = await stravaActivityService.getActivities(page, 30, startDate, endDate);
                if (activities.length === 0) {
                    hasMore = false;
                    break;
                }
                allActivities = [...allActivities, ...activities];
                
                // Check if we've reached the date limit
                if (startDate && activities[activities.length - 1].start_date < startDate) {
                    hasMore = false;
                }
                
                page++;
                
                // Safety limit to prevent infinite loops
                if (page > 10) {
                    hasMore = false;
                }
            }

            console.log(`Loaded ${allActivities.length} total activities`);
            setActivities(allActivities);
            setError(null);
        } catch (err) {
            setError('Failed to load activities. Please try again later.');
            console.error('Error loading activities:', err);
        } finally {
            setLoading(false);
        }
    }, [settings.dateRange]);

    useEffect(() => {
        const handleRateLimit = (status) => {
            setRateLimitStatus(status);
            console.log(`API Rate Limit: ${status.current}/${status.limit} requests used`);
            if (status.current >= status.limit) {
                setError('Rate limit reached. Please try again later.');
                console.warn('⚠️ API Rate limit reached!');
            }
        };

        stravaActivityService.setRateLimitCallback(handleRateLimit);
        loadActivities();
        
        return () => {
            stravaActivityService.setRateLimitCallback(null);
        };
    }, [loadActivities]);

    const handleDownload = async () => {
        if (!gridRef.current) return;

        // Hide the burger menu and download button temporarily
        const burgerMenu = document.querySelector('.burger-menu');
        const downloadButton = document.querySelector('.download-button');
        if (burgerMenu) burgerMenu.style.display = 'none';
        if (downloadButton) downloadButton.style.display = 'none';

        try {
            // Set background color explicitly
            const gridElement = gridRef.current;
            const originalBackground = gridElement.style.background;
            gridElement.style.background = settings.colors.background;

            const canvas = await html2canvas(gridRef.current, {
                backgroundColor: settings.colors.background,
                scale: 2, // Higher quality
                logging: false,
                useCORS: true,
                allowTaint: true
            });

            // Restore original background
            gridElement.style.background = originalBackground;

            // Create download link
            const link = document.createElement('a');
            link.download = `strava-runs-${new Date().toISOString().split('T')[0]}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();

            // Restore the burger menu and download button
            if (burgerMenu) burgerMenu.style.display = 'block';
            if (downloadButton) downloadButton.style.display = 'block';
        } catch (err) {
            console.error('Error generating image:', err);
            setError('Failed to generate image. Please try again.');
        }
    };

    // Filter for runs only
    const runActivities = activities.filter(activity => activity.type === 'Run');

    if (error) {
        return <div className="error">{error}</div>;
    }

    return (
        <div className="activity-grid-container" style={{ background: settings.colors.background }}>
            <BurgerMenu 
                isOpen={isMenuOpen} 
                onClose={toggleMenu}
            >
                <ActivityGridSettings 
                    settings={settings} 
                    onSettingsChange={setSettings} 
                />
            </BurgerMenu>
            <button className="download-button" onClick={handleDownload}>
                Download Grid
            </button>
            {loading && <div className="loading">Loading activities...</div>}
            <div ref={gridRef} className="activity-grid" style={{ background: settings.colors.background }}>
                <h1 className="grid-title" style={{ color: settings.colors.text }}>{settings.title}</h1>
                {runActivities.map((activity) => (
                    <ActivityCard 
                        key={activity.id} 
                        activity={activity} 
                        settings={settings}
                    />
                ))}
            </div>
        </div>
    );
};

export default ActivityGrid; 